import { 
    PhoenixUser, 
    PhoenixUserGroup, 
    isPhoenixUser 
} from 'phoenix/domain';
import { AuthService } from 'phoenix/services';
import { IStorageAccessor } from 'util/domain';

export interface IUserHistoryDataStore {
    readonly users: IShareBaseUserHistory[];
    readonly userGroups: IShareBaseUserGroupHistory[];
    readonly recents: HistoryItem[];
    addToHistory(item: PhoenixUser | PhoenixUserGroup): void ;
}

interface HistoryItem {
    time: Date;
    isRecent: boolean;
    deployment: number;
}
export type IShareBaseUserHistory = PhoenixUser & HistoryItem;
export type IShareBaseUserGroupHistory = PhoenixUserGroup & HistoryItem;
interface HistoryStore<T extends HistoryItem> {
    [deploymentId:string]: T[]
}
const StorageKeys = {
    RECENT_USERS : 'sb-user-select-history',
    RECENT_GROUPS: 'sb-usergroups-select-history'
};
/**
 * @ngdoc service
 * @name  sharebase.sbUserHistoryDataStore
 *
 * @description
 * A datastore used to interact with user's interaction history.
 *
**/
export function UserHistoryDataStore(
    pxAuthService: AuthService, 
    localStorageService:IStorageAccessor
): IUserHistoryDataStore {
    const MAX_HISTORY_ITEMS = 10;
    const recentsKey = 'dep' + (pxAuthService.deploymentId || 0);
    let _users:HistoryStore<IShareBaseUserHistory> = {};
    let _userGroups:HistoryStore<IShareBaseUserGroupHistory> = {};    

    loadHistory();

    let store:IUserHistoryDataStore = {
        /**
		 * @ngdoc method
		 * @name sbUserHistoryDataStore#addToHistory
		 * @methodOf sharebase.sbUserHistoryDataStore
		 * @kind function
		 *
		 * @description
		 * Adds the item to the local store of history.
		 *
		 */
        addToHistory,

        /**
		 * @ngdoc property
		 * @name sbUserHistoryDataStore#users
		 * @propertyOf sharebase.sbUserHistoryDataStore
		 * @kind property
		 *
		 * @description
		 * A user's recent selected users
		 *
		 * @returns {any[]} An array of users.
		 */
        get users(){ return _users[recentsKey] || []; },

        /**
		 * @ngdoc property
		 * @name sbUserHistoryDataStore#userGroups
		 * @propertyOf sharebase.sbUserHistoryDataStore
		 * @kind property
		 *
		 * @description
		 * A user's recent selected user groups
		 *
		 * @returns {any[]} An array of user groups.
		 */
        get userGroups(){ return _userGroups[recentsKey] || []; },
        get recents() {
            return (store.users as HistoryItem[])
                .concat(store.userGroups)
                .sort((a, b) => b.time.getTime() - a.time.getTime());
        }
    };

    return store;

    function addToHistory(item: PhoenixUser | PhoenixUserGroup) {
        if(!item){ return;}
        let itemIsUser = isPhoenixUser(item);
        let storageKey = itemIsUser ? StorageKeys.RECENT_USERS : StorageKeys.RECENT_GROUPS;
        let targetId = itemIsUser ? 'UserId' : 'UserGroupId';

        let historyItem = itemIsUser ? item as IShareBaseUserHistory  : item as IShareBaseUserGroupHistory;
        historyItem.time = new Date();
        historyItem.isRecent = true;
        historyItem.deployment = pxAuthService.deploymentId || 0;

        let store:HistoryStore<HistoryItem> = itemIsUser ? _users : _userGroups;        
        if(!store[recentsKey]){
            store[recentsKey] = [];
        }

        
        let history:HistoryItem[] = store[recentsKey];
        // remove any previous duplicate entries
        history = history.filter(item => item[targetId] !== historyItem[targetId]);       
        // add it to the top
        history.unshift(historyItem);
        // only save the last 10
        history = history.length > MAX_HISTORY_ITEMS ? history.splice(0, MAX_HISTORY_ITEMS) : history;
        store[recentsKey] = history;

        localStorageService.setStoredValue(storageKey, JSON.stringify(store));
    }

    function loadHistory() {
        let storedUsers = localStorageService.getStoredValue(StorageKeys.RECENT_USERS);
        let storedUserGroups = localStorageService.getStoredValue(StorageKeys.RECENT_GROUPS);

        _users = pruneDupes(storedUsers ? (JSON.parse(storedUsers) as HistoryStore<IShareBaseUserHistory>) : {});
        _userGroups = pruneDupes(storedUserGroups ? (JSON.parse(storedUserGroups) as HistoryStore<IShareBaseUserGroupHistory>) : {});

        let usersByDeployment = Object.keys(_users).map(deploymentName => _users[deploymentName] as HistoryItem[]);
        let groupsByDeployment = Object.keys(_userGroups).map(deploymentName => _userGroups[deploymentName] as HistoryItem[]);

        usersByDeployment
            .concat(groupsByDeployment)
            .forEach(deployment  =>  {
                deployment.forEach(historyItem => {
                    historyItem.time = new Date(historyItem.time);
                });
            });
            
    }

    function pruneDupes<T extends HistoryItem>(store:HistoryStore<T>){
        Object.keys(store)
            .forEach(depId => {
                let deployment = store[depId];
                let pruned = deployment.reduce((all, item) => {
                    let prop = isUserGroupHistory(item) ? 'UserGroupId' : 'UserId';
                    all[item[prop]] = item;
                    return all;
                }, {} as { [key:string]:T} );

                let prunedSorted = Object.keys(pruned)
                    .map(k => pruned[k])
                    .sort((a, b) => {
                        let aTime = new Date(a.time);
                        let bTime = new Date(b.time);
                        if(aTime === bTime){
                            return 0;
                        }
                        else {
                            return aTime > bTime ? -1 : 1;
                        }
                    });
                
                store[depId] = prunedSorted;
            });
        
        return store;
    }

    function isUserGroupHistory(item:HistoryItem): item is IShareBaseUserGroupHistory {
        return angular.isDefined((item as IShareBaseUserGroupHistory).UserGroupId);
    }
}

UserHistoryDataStore.$inject = [
    'pxAuthService',
    'localStorageService'
];


// WEBPACK FOOTER //
// ./src/sharebase/dataStores/userHistoryDataStore.ts